/**
 * Maps audio file IDs to their names
 * in the `waiting_audio_id` and `whisper_audio_id` fields
 * of each inbound queue object.
 *
 * Reasoning: correct table functionality (filter, bulk set, sort)
 *
 * @param {Object} params
 * @param {Array} params.audioFiles - List of audio file objects ({ id, name })
 * @param {Array} params.inboundQueues - List of inbound queue objects
 * @returns {Array} New array of inbound queues with names substituted for IDs
 */
export const withAudioFileName = ({ audioFiles, inboundQueues }) => {
  // Build an ID-to-name lookup map for reverse access
  const audioMap = Object.fromEntries(
    (audioFiles || []).map((file) => [file.id, file.name])
  );

  return (inboundQueues || []).map((inboundQueue) => {
    const updatedQueue = { ...inboundQueue };

    if (inboundQueue.waiting_audio_id) {
      updatedQueue.waiting_audio_id = audioMap[inboundQueue.waiting_audio_id];
    }

    if (inboundQueue.whisper_audio_id) {
      updatedQueue.whisper_audio_id = audioMap[inboundQueue.whisper_audio_id];
    }

    return updatedQueue;
  });
};

/**
 * Maps audio file names back to their corresponding IDs
 * in the `waiting_audio_id` and `whisper_audio_id` fields
 * of each inbound queue object.
 *
 * Reasoning: correct table functionality (filter, bulk set, sort)
 *
 * @param {Object} params
 * @param {Array} params.audioFiles - List of audio file objects ({ id, name })
 * @param {Array} params.inboundQueues - List of inbound queue objects
 * @returns {Array} New array of inbound queues with IDs substituted for names
 */
export const withAudioFileId = ({ audioFiles, inboundQueues }) => {
  // Build a name-to-ID lookup map for fast access
  const audioMap = Object.fromEntries(
    (audioFiles || []).map((file) => [file.name, file.id])
  );

  return (inboundQueues || []).map((inboundQueue) => {
    const updatedQueue = { ...inboundQueue };

    if (inboundQueue.waiting_audio_id) {
      updatedQueue.waiting_audio_id = audioMap[inboundQueue.waiting_audio_id];
    }

    if (inboundQueue.whisper_audio_id) {
      updatedQueue.whisper_audio_id = audioMap[inboundQueue.whisper_audio_id];
    }

    return updatedQueue;
  });
};
